import React, {useCallback, useEffect, useRef, useState} from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import List from '@mui/joy/List';
import ListDivider from '@mui/joy/ListDivider';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import MailAPI, { Mail } from '../../api.ts';
import {Delete} from '@mui/icons-material';
import { DialogContent, DialogTitle, Divider, LinearProgress, Modal, ModalDialog } from '@mui/joy';

const api = new MailAPI();

type MailListProps = {
  onMailCountUpdate?: (count: number) => void;
  deleteAll?: number,
  updateMail?: number
};

export default function EmailList({ onMailCountUpdate, deleteAll, updateMail }: MailListProps) {
  const [mails, setMails] = useState<Array<Mail>>([]);
  const intervalId = useRef<Number | null>(null);
  const [readMailIndex, setReadMailIndex] = useState(-1);

  const fetchMails = useCallback(async () => {
    try {
      const fetchedMails = await api.getInbox();
      const sortedMails = fetchedMails.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA;
      });
      
      setMails(sortedMails);

      if (onMailCountUpdate) {
        onMailCountUpdate(fetchedMails.length);
      }

    } catch (err) {
      console.error(err);
    }
  }, [onMailCountUpdate]);

  const deleteMails = useCallback(async (mailId?: string) => {
    await api.deleteMails(mailId);
    await fetchMails();
  }, [fetchMails]);

  useEffect(() => {
    if (!intervalId.current) {
      fetchMails();

      intervalId.current = setInterval(() => {
        fetchMails();
      }, 5000);
    }
  }, [fetchMails]);

  useEffect(() => {
    if (deleteAll && deleteAll > 0) {
      deleteMails();
    }
  }, [deleteAll, deleteMails]);

  useEffect(() => {
    if (updateMail && updateMail > 0) {
      setMails([]);
      fetchMails();
    }
  }, [updateMail, fetchMails]);

  return (
    <Box>
      <LinearProgress thickness={1} sx={{ mb: 2 }} />
      <List
        sx={{
          [`& .${listItemButtonClasses.root}.${listItemButtonClasses.selected}`]: {
            borderLeft: '2px solid',
            borderLeftColor: 'var(--joy-palette-primary-outlinedBorder)',
          },
        }}
      >
      {mails.map((mail, index) => (
        <React.Fragment key={index}>
          <ListItem onClick={() => setReadMailIndex(index)}>
            <ListItemButton
              selected={index %2 === 0}
              color='neutral'
              sx={{ p: 2 }}
            >
              <Box sx={{ pl: 2, width: '100%' }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography level="body-xs">{mail.sender.address}</Typography>
                  </Box>
                  <Typography level="body-xs" textColor="text.tertiary">
                    {new Date(mail.date).toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                      {mail.subject}
                    </Typography>
                  </Box>
                  <Delete onClick={(e) => {
                    e.stopPropagation();
                    deleteMails(mail.id);
                  }} style={{color: '#C41C1C'}} />
                </Box>
              </Box>
            </ListItemButton>
          </ListItem>
          <ListDivider sx={{ m: 0 }} />
        </React.Fragment>
      ))}
      </List>

      <Modal open={readMailIndex > -1} onClose={() => setReadMailIndex(-1)}>
        <ModalDialog sx={{ minWidth: '700px' }}>
          <DialogTitle>{mails[readMailIndex] ? mails[readMailIndex].subject : 'Loading...'}</DialogTitle>
          <Divider></Divider>
          <DialogContent>
            <div
              dangerouslySetInnerHTML={{ __html: mails[readMailIndex] ? mails[readMailIndex].html : '' }}
            />
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
}